import {
  safeGetSessionItem,
  safeRemoveSessionItem,
  safeRemoveSessionItemsByPrefix,
  safeSetSessionItem,
} from '@helpers/storage'

import type { MessageToAccountantFormValues } from '@components/forms/MessageToAccountantForm'

// Session storage so a draft survives a reload but not a closed tab.
// Keyed per user because it also outlives a logout, and must stay invisible to whoever logs in next.
export const DRAFT_KEY_PREFIX = 'tms_message_to_accountant_draft_'

function getDraftKey(userId: User['id']) {
  return `${DRAFT_KEY_PREFIX}${userId}`
}

function isDraftGuard(value: unknown): value is MessageToAccountantFormValues {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const { topic, message } = value as Partial<MessageToAccountantFormValues>
  return (topic === null || typeof topic === 'number') && typeof message === 'string'
}

/** @returns the stored draft, or null when there is none or it is unreadable */
export function loadDraft(userId: User['id']): Nullable<MessageToAccountantFormValues> {
  const stored = safeGetSessionItem(getDraftKey(userId))
  if (!stored) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(stored)
    return isDraftGuard(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Takes partial values so it can consume a react-hook-form subscription payload as is.
 *
 * The message length is deliberately uncapped - an oversized one simply fails the quota guard and
 * goes unsaved, which is the same outcome a cap would produce with more moving parts.
 */
export function saveDraft(userId: User['id'], { topic = null, message = '' }: Partial<MessageToAccountantFormValues>) {
  if (topic === null && !message.trim()) {
    clearDraft(userId)
    return
  }

  safeSetSessionItem(getDraftKey(userId), JSON.stringify({ topic, message }))
}

export function clearDraft(userId: User['id']) {
  safeRemoveSessionItem(getDraftKey(userId))
}

/** Sweeps every user's draft - logout has no user id to target, and this keeps orphans from piling up. */
export function clearAllDrafts() {
  safeRemoveSessionItemsByPrefix(DRAFT_KEY_PREFIX)
}
